import { useEffect, useRef, useState } from "react";
import { fetchNote, saveNote } from "./notes";
import { LockIcon } from "./Icons";

const SAVE_DEBOUNCE_MS = 900;

/** Personal notes for one conversation — private scratch space only the
 * viewer can see, same idea as Teams' per-chat Notes tab. Auto-saves a
 * short while after you stop typing, same pattern as any "no explicit
 * save button" notes app. */
export default function NotesPanel({
  conversationId,
  title,
  onClose,
}: {
  conversationId: string;
  title: string;
  onClose: () => void;
}) {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const saveTimer = useRef<number | null>(null);
  const latestRef = useRef("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchNote(conversationId)
      .then((text) => {
        if (cancelled) return;
        setContent(text);
        latestRef.current = text;
      })
      .catch((e) => console.warn("note load failed", e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [conversationId]);

  const scheduleSave = (next: string) => {
    latestRef.current = next;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      setStatus("saving");
      try {
        await saveNote(conversationId, latestRef.current);
        setStatus("saved");
      } catch (e) {
        console.warn("note save failed", e);
        setStatus("error");
      }
    }, SAVE_DEBOUNCE_MS);
  };

  return (
    <aside className="notes-panel">
      <div className="modal-head">
        <b>Notes</b>
        <button className="hbtn" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      <div className="notes-panel__sub">
        <LockIcon size={12} />
        <span>Only you can see these — private notes for {title}.</span>
      </div>
      <div className="notes-panel__body">
        {loading ? (
          <div className="sect-empty">Loading…</div>
        ) : (
          <textarea
            className="notes-panel__textarea"
            value={content}
            placeholder="Jot down anything you want to remember about this conversation…"
            onChange={(e) => {
              setContent(e.target.value);
              scheduleSave(e.target.value);
            }}
          />
        )}
      </div>
      <div className="notes-panel__foot">
        {status === "saving" && <span>Saving…</span>}
        {status === "saved" && <span>Saved</span>}
        {status === "error" && <span className="notes-panel__err">Couldn't save — will retry as you type</span>}
      </div>
    </aside>
  );
}
